import { ProcessGameObject, Scene, Vector3, vector3Zero } from "realms-engine";
import { Render3DGameObject } from "realms-engine-browser";
import THREE from "realms-three";

interface ThreeAnimation {
  clip: THREE.AnimationClip;
  timeScale?: number;
  clampWhenFinished?: boolean;
  offset?: Vector3;
}

interface ThreeAnimationMap {
  [animationName: string]: ThreeAnimation;
}

export class ThreeAnimationGameObject<
  T extends ThreeAnimationMap
> extends ProcessGameObject {
  private animationActionMap: Map<
    Exclude<keyof T, number | symbol>,
    THREE.AnimationAction
  > = new Map();
  private activeAnimationAction: THREE.AnimationAction | null = null;
  private activeAnimationName: Exclude<keyof T, number | symbol> | null = null;
  private mixer: THREE.AnimationMixer | null = null;

  constructor(
    private animationMap: T,
    private options: { fadeOutDuration?: number } = {}
  ) {
    super();
  }

  override onInit(scene: Scene): void {
    const parent = this.getParent();
    if (!(parent instanceof Render3DGameObject)) {
      throw new Error(
        `Animation GameObject is not the child of a Render3DGameObject`
      );
    }
    this.setTarget(parent.getThreeObject());
    parent.addListener("onThreeObjectChange", this.setTarget);
  }

  override onRemove(scene: Scene): void {
    const parent = this.getParent();
    if (!(parent instanceof Render3DGameObject)) {
      throw new Error(
        `Animation GameObject is removed from a non Render3DGameObject`
      );
    }
    parent.removeListener("onThreeObjectChange", this.setTarget);
  }

  /**
   * Updates animation map
   */
  updateAnimationMap(animationMap: T) {
    this.animationMap = { ...this.animationMap, ...animationMap };
  }

  /*
   * Sets a new target for the animation component
   */
  private setTarget = (target: THREE.Object3D) => {
    if (this.mixer) {
      this.mixer.stopAllAction();
    }
    this.mixer = new THREE.AnimationMixer(target);
    this.loadAnimations(this.animationMap, this.mixer);
    if (this.activeAnimationName) {
      this.play(this.activeAnimationName);
    }
  };

  /*
   * Play the given animation
   */
  play(
    animation: Exclude<keyof T, number | symbol>,
    options: {
      duration?: number | null;
      // If speed is passed, duration is ignored
      speed?: number | null;
      clampWhenFinished?: boolean;
      fadeOutDuration?: number;
    } = {}
  ) {
    const action = this.animationActionMap.get(animation);
    if (!action) {
      throw new Error(
        `No animation named ${animation} exists. Available Animations: ${Array.from(
          this.animationActionMap.keys()
        ).join(", ")}`
      );
    }

    // Already playing animation so ignore
    if (this.activeAnimationAction === action) {
      return;
    }
    const fadeOutDuration =
      options.fadeOutDuration ?? this.options.fadeOutDuration ?? 0.1;

    if (this.activeAnimationAction !== null) {
      this.activeAnimationAction.fadeOut(fadeOutDuration);
    }
    this.activeAnimationName = animation;
    this.activeAnimationAction = action
      .reset()
      .setEffectiveWeight(1)
      .fadeIn(fadeOutDuration);
    if (typeof options.duration === "number") {
      // Duration is in seconds so convert it to millisceconds
      const clipDuration = this.activeAnimationAction.getClip().duration * 1000;
      const timeScale = clipDuration / options.duration;
      this.activeAnimationAction.timeScale = timeScale;
    }
    if (typeof options.speed === "number") {
      this.activeAnimationAction.timeScale = options.speed;
    }
    if (options.clampWhenFinished) {
      this.activeAnimationAction.clampWhenFinished = true;
      this.activeAnimationAction.loop = THREE.LoopOnce;
    }
    // Set offset
    const anim = this.animationMap[animation];
    if (anim.offset) {
      (this.getParent() as Render3DGameObject).setOffset(anim.offset);
    } else {
      (this.getParent() as Render3DGameObject).setOffset(vector3Zero());
    }

    this.activeAnimationAction.play();
  }

  /**
   * Load animations
   */
  private loadAnimations(animationMap: T, mixer: THREE.AnimationMixer) {
    for (const [animName, animation] of Object.entries(animationMap)) {
      const action = mixer.clipAction(animation.clip);
      if (animation.timeScale !== undefined) {
        action.timeScale = animation.timeScale;
      }
      if (animation.clampWhenFinished) {
        action.clampWhenFinished = true;
        action.loop = THREE.LoopOnce;
      }
      this.animationActionMap.set(
        animName as Exclude<keyof T, number | symbol>,
        action
      );
    }
  }

  override step(delta: number): void {
    if (this.mixer) {
      this.mixer.update(delta / 1000);
    }
  }
}
